/**
 * O comportamento **obrigatorio** do `api-client` diante de `429` — `API §9`, os seis itens,
 * um a um:
 *
 * 1. ler `Retry-After` (segundos) e **nunca inventar intervalo proprio**;
 * 2. jitter aleatorio de +-20% antes de repetir ("sem jitter, 300 celulares voltando do mesmo
 *    apagao de rede batem juntos");
 * 3. no maximo 2 tentativas em `GET`; 3 em `POST` idempotente com `Idempotency-Key`; em `POST`
 *    sem chave, **nunca** repetir;
 * 4. limite de OTP ou de login: **nao** repetir — a tela mostra contagem regressiva;
 * 5. a cada `429`, emitir telemetria `rate_limited` com a rota;
 * 6. com `RateLimit.remaining <= 10%`, reduzir preventivamente a frequencia de polling.
 *
 * Os itens 1 a 4 sao decisao deste arquivo. O 5 e o gancho `onRateLimited` da configuracao. O
 * 6 e `pollingIntervalFor`, que a tela de status de pagamento e a fila do Painel consomem.
 */

/** `RateLimit: limit=60, remaining=0, reset=42` (`API §9`). */
export interface RateLimitSnapshot {
  readonly limit: number;
  readonly remaining: number;
  /** Segundos ate a janela reiniciar. */
  readonly reset: number;
}

/**
 * `Retry-After` em segundos. Aceita as duas formas da RFC 9110: delta-seconds e HTTP-date.
 *
 * `now` entra por parametro porque a forma HTTP-date e uma subtracao de relogios — e um teste
 * que dependesse de `Date.now()` seria intermitente por construcao.
 */
export function parseRetryAfterSeconds(header: string | null, now: number): number | null {
  if (header === null) return null;
  const trimmed = header.trim();
  if (trimmed === '') return null;

  if (/^\d+$/.test(trimmed)) return Number(trimmed);

  const timestamp = Date.parse(trimmed);
  if (Number.isNaN(timestamp)) return null;
  // Data no passado (relogios dessincronizados) vira 0, e nao negativo: o cliente repete ja.
  return Math.max(0, Math.round((timestamp - now) / 1000));
}

/** O header `RateLimit`. Campo ausente ou nao-numerico invalida o conjunto — meio dado mente. */
export function parseRateLimit(header: string | null): RateLimitSnapshot | null {
  if (header === null) return null;

  const fields = new Map<string, number>();
  for (const part of header.split(',')) {
    const [rawName, rawValue] = part.split('=');
    if (rawName === undefined || rawValue === undefined) continue;
    const value = Number(rawValue.trim());
    if (!Number.isFinite(value)) continue;
    fields.set(rawName.trim().toLowerCase(), value);
  }

  const limit = fields.get('limit');
  const remaining = fields.get('remaining');
  const reset = fields.get('reset');
  if (limit === undefined || remaining === undefined || reset === undefined) return null;

  return { limit, remaining, reset };
}

/** `API §9`, item 2: +-20% sobre o intervalo que o servidor pediu. */
export const RETRY_JITTER_RATIO = 0.2;

/** Aplica o jitter e devolve **milissegundos**. `random()` vem da configuracao. */
export function jitteredDelayMs(seconds: number, random: () => number): number {
  const factor = 1 + (random() * 2 - 1) * RETRY_JITTER_RATIO;
  return Math.max(0, Math.round(seconds * 1000 * factor));
}

/**
 * As rotas em que `429` **nao** e repetido (`API §9`, item 4): OTP e login.
 *
 * Repetir aqui e pior que inutil — cada tentativa de OTP custa um SMS de verdade (`ADR-0009`:
 * "Custo de SMS e real") e o limite de login e progressivo, entao a repeticao automatica
 * empurra o proprio usuario para o bloqueio. A tela mostra contagem regressiva.
 */
export const NO_RETRY_PATH_PATTERNS: readonly RegExp[] = [
  /^\/v\d+\/auth\/otp\//,
  /^\/v\d+\/auth\/staff\/login$/,
];

export interface RetryBudgetInput {
  readonly method: string;
  readonly path: string;
  /** A requisicao leva `Idempotency-Key`? */
  readonly hasIdempotencyKey: boolean;
  /** A requisicao leva `If-Match`? */
  readonly hasIfMatch: boolean;
}

/**
 * Quantas **retentativas** (alem da primeira) esta requisicao pode fazer.
 *
 * `API §9` fala de `GET` e de `POST`. Os outros metodos saem do `API §3.1`, que os nomeia:
 * *"Nao exigem [chave] (sao naturalmente idempotentes): `PUT`, `DELETE`, `GET`, `HEAD`, e
 * `PATCH /v1/cart` (protegido por `If-Match`, que ja resolve)"*. Um `PUT` repetido produz o
 * mesmo estado; um `PATCH` com `If-Match` ou vence a mesma versao ou responde `409` — nos dois
 * casos nada duplica. `PATCH` **sem** `If-Match` nao tem essa garantia e nao repete.
 *
 * `POST` sem `Idempotency-Key` fica em `0` sempre: e o pedido duplicado, a cobranca duplicada
 * e o estorno duplicado da tabela do `API §3.1`.
 */
export function retryBudget(input: RetryBudgetInput): number {
  if (NO_RETRY_PATH_PATTERNS.some((pattern) => pattern.test(input.path))) return 0;

  const method = input.method.toUpperCase();
  if (method === 'GET' || method === 'HEAD') return 2;
  if (method === 'PUT' || method === 'DELETE') return 2;
  if (method === 'PATCH') return input.hasIfMatch ? 2 : 0;
  if (method === 'POST') return input.hasIdempotencyKey ? 3 : 0;
  return 0;
}

/** `API §9`, item 6: a partir de 10% do orcamento, o polling dobra o intervalo. */
export const RATE_LIMIT_SLOWDOWN_RATIO = 0.1;

/**
 * O intervalo de polling a usar agora. Sem snapshot, e o intervalo base.
 *
 * Serve as duas telas que fazem polling no MVP: o status de pagamento Pix
 * (`PIX_POLL_AFTER_SECONDS`) e a fila do Painel da Loja (5 s — `ADR-0013`).
 */
export function pollingIntervalFor(baseMs: number, snapshot: RateLimitSnapshot | null): number {
  if (snapshot === null || snapshot.limit <= 0) return baseMs;
  const ratio = snapshot.remaining / snapshot.limit;
  return ratio <= RATE_LIMIT_SLOWDOWN_RATIO ? baseMs * 2 : baseMs;
}
